// @ts-check
// WHERE IDENTITY COMES FROM — the one seam between this client and sign-in.
//
// SERVER-SIDE SESSIONS (PLATFORM-PLAN-9). The Academy's server is a confidential
// OIDC client of the shared realm (academy-bff) and keeps every token in Redis;
// this tab holds an HttpOnly cookie and nothing else. The adapter
// (./bffTokenSource) asks the server who is signed in and navigates through it
// to sign in or out. The server still validates every request as a resource
// server — the token it takes out of the session goes through the same
// JwtBearer validation a bearer header does, Person match included.
//
// The interface outlived two identity providers and now a token model: Entra →
// Keycloak was a client-side-only change, and so is browser tokens → server
// sessions. Nothing outside this module names the adapter. Screens, hooks, the
// ui-kit's AuthProvider and services call the functions at the bottom and cannot
// tell what is answering.

import { bffTokenSource, attemptSilentSignIn, canAttemptSilentSignIn } from './bffTokenSource';

/**
 * @typedef {object} Actor
 * @property {string} id            Keycloak user id ("sub").
 * @property {string} displayName
 * @property {string} [email]
 * @property {string[]} roles       Keycloak client role values. They decide nothing any more.
 * @property {string[]} permissions Screen-permission patterns from Nexus ("academy.my.view",
 *   "academy.*", ...), passed through from /api/bff/user. ui-kit's canScreen matches these.
 * @property {{ organisationId: string, name: string }[]} [managerOf]  The organisations this
 *   person manages (GET /my/access). Empty for everybody else. Drives the company portal's
 *   organisation picker.
 * @property {boolean} [isLearner]  Whether this person has a learner profile (GET /my/access).
 * @property {boolean} [passwordReset]  Whether "Email me a password reset link" can work here
 *   (/api/bff/user says if the server reaches Nexus). false hides the menu item.
 *
 * @typedef {object} TokenSource
 * @property {() => Promise<string|null>} getAccessToken  Always null now: the session cookie authenticates.
 * @property {() => Promise<Actor|null>} getActor
 * @property {() => Promise<void>} signIn
 * @property {() => Promise<void>} signOut
 * @property {boolean} interactive  False when there is nothing for the user to sign in to.
 */

/** There is always somewhere to sign in: the server. */
export const isInteractive = true;

/** Kept for the interface's shape. Null means: send no Authorization header. */
export const getAccessToken = () => bffTokenSource.getAccessToken();

/** @returns {Promise<Actor|null>} */
export const getActor = () => bffTokenSource.getActor();

export const signIn = () => bffTokenSource.signIn();

export const signOut = () => bffTokenSource.signOut();

export { attemptSilentSignIn, canAttemptSilentSignIn };
